// App-wide error boundary integrated with Sentry. Renders a styled fallback (no
// system dialog, RULE #2). Copy is bilingual; it lives outside LanguageProvider,
// so language is picked from the browser rather than context.
import { Component, type ErrorInfo, type ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { logError } from '../lib/logger';
import styles from './ErrorBoundary.module.css';

const COPY = {
  pl: {
    title: 'BŁĄD KRYTYCZNY',
    desc: 'Trwają prace naprawcze. Odśwież stronę, aby kontynuować.',
    reload: 'ODŚWIEŻ',
  },
  en: {
    title: 'CRITICAL ERROR',
    desc: 'Something broke. Reload the page to continue.',
    reload: 'RELOAD',
  },
} as const;

function pickLang(): 'pl' | 'en' {
  if (typeof navigator !== 'undefined' && navigator.language.startsWith('pl')) return 'pl';
  return 'en';
}

interface Props {
  readonly children: ReactNode;
}
interface State {
  readonly hasError: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    void logError(error, 'ErrorBoundary', info.componentStack ?? undefined);
  }

  private readonly handleReload = (): void => {
    window.location.reload();
  };

  override render(): ReactNode {
    const t = COPY[pickLang()];
    const fallback = (
      <div className={styles.screen} role="alert">
        <div className={styles.box}>
          <h1 className={styles.title}>{t.title}</h1>
          <p className={styles.desc}>{t.desc}</p>
          <button type="button" className={styles.button} onClick={this.handleReload}>
            {t.reload}
          </button>
        </div>
      </div>
    );

    return (
      <Sentry.ErrorBoundary
        fallback={fallback}
        onError={(error, componentStack) => {
          void logError(error, 'ErrorBoundary', componentStack ?? undefined);
        }}
      >
        {this.state.hasError ? fallback : this.props.children}
      </Sentry.ErrorBoundary>
    );
  }
}
